import { getActiveStore } from "@/lib/scope";
import { useEffect, useMemo, useState } from "react";
import { api, apiError } from "@/lib/pos-api";
import { rupiah } from "@/lib/format";
import { toast } from "sonner";
import { format } from "date-fns";
import { id as idLocale } from "date-fns/locale";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Clock, CalendarIcon, Printer } from "lucide-react";
import { printClosing } from "@/lib/printShift";

const fmt = (d) => format(d, "yyyy-MM-dd");
const label = (d) => format(d, "d MMM yyyy", { locale: idLocale });

export default function Shifts() {
  const [shifts, setShifts] = useState([]);
  const [cashiers, setCashiers] = useState([]);
  const [storeName, setStoreName] = useState("");
  const [range, setRange] = useState(null);
  const [calOpen, setCalOpen] = useState(false);
  const [userId, setUserId] = useState("all");

  const start = range?.from ? fmt(range.from) : null;
  const end = range?.to ? fmt(range.to) : (range?.from ? fmt(range.from) : null);

  const load = async () => {
    try {
      const params = {};
      if (start && end) { params.start = start; params.end = end; }
      if (userId !== "all") params.user_id = userId;
      const { data } = await api.get("/shifts", { params });
      setShifts(data);
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [start, end, userId]);

  useEffect(() => {
    api.get("/users").then((r) => setCashiers(r.data)).catch(() => {});
    api.get("/stores").then((r) => {
      const sid = getActiveStore();
      const st = r.data.find((s) => s.id === sid) || r.data.find((s) => s.is_default) || r.data[0];
      if (st) setStoreName(st.name);
    }).catch(() => {});
  }, []);

  const dt = (v) => (v ? new Date(v).toLocaleString("id-ID") : "-");

  const preset = (days, mode) => {
    const now = new Date();
    if (mode === "today") setRange({ from: now, to: now });
    else if (mode === "month") setRange({ from: new Date(now.getFullYear(), now.getMonth(), 1), to: now });
    else { const f = new Date(now); f.setDate(f.getDate() - (days - 1)); setRange({ from: f, to: now }); }
    setCalOpen(false);
  };

  const rangeText = range?.from
    ? (range.to && fmt(range.to) !== fmt(range.from) ? `${label(range.from)} — ${label(range.to)}` : label(range.from))
    : "Semua tanggal";

  const totals = useMemo(() => {
    const closed = shifts.filter((s) => s.status === "closed");
    return {
      count: shifts.length,
      revenue: closed.reduce((a, s) => a + (s.summary?.revenue || 0), 0),
      selisih: closed.reduce((a, s) => a + (s.selisih || 0), 0),
    };
  }, [shifts]);

  return (
    <>
      <PageHeader title="Riwayat Shift" subtitle="Buka/tutup shift kasir & selisih kas">
        <div className="flex flex-wrap items-center gap-2">
          <Select value={userId} onValueChange={setUserId}>
            <SelectTrigger data-testid="shift-cashier-filter" className="w-[170px] h-10 border-ui-line bg-ui-card">
              <SelectValue placeholder="Semua kasir" />
            </SelectTrigger>
            <SelectContent className="bg-ui-card border-ui-line text-ui-text">
              <SelectItem value="all">Semua Kasir</SelectItem>
              {cashiers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Popover open={calOpen} onOpenChange={setCalOpen}>
            <PopoverTrigger asChild>
              <Button data-testid="shift-date-btn" variant="outline" className="border-ui-line bg-ui-card hover:bg-ui-card gap-2 h-10">
                <CalendarIcon className="h-4 w-4 text-brand-readable" />
                <span className="text-sm">{rangeText}</span>
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-auto p-0 bg-ui-card border-ui-line text-ui-text" data-testid="shift-calendar">
              <div className="flex flex-wrap gap-1.5 p-3 border-b border-ui-line">
                <Chip onClick={() => { setRange(null); setCalOpen(false); }}>Semua</Chip>
                <Chip onClick={() => preset(0, "today")}>Hari ini</Chip>
                <Chip onClick={() => preset(7)}>7 Hari</Chip>
                <Chip onClick={() => preset(30)}>30 Hari</Chip>
                <Chip onClick={() => preset(0, "month")}>Bulan Ini</Chip>
              </div>
              <Calendar mode="range" selected={range} onSelect={(r) => setRange(r)} numberOfMonths={2} locale={idLocale} />
            </PopoverContent>
          </Popover>
        </div>
      </PageHeader>
      <div className="flex-1 overflow-y-auto p-5 space-y-4" data-testid="shifts-page">
        <div className="grid grid-cols-3 gap-3">
          <Stat label="Jumlah Shift" value={totals.count} />
          <Stat label="Total Penjualan (tutup)" value={rupiah(totals.revenue)} />
          <Stat label="Total Selisih" value={rupiah(totals.selisih)} cls={totals.selisih === 0 ? "" : totals.selisih > 0 ? "text-accent-readable" : "text-danger-readable"} />
        </div>
        <div className="rounded-xl border border-ui-line bg-ui-soft overflow-x-auto">
          <table className="w-full text-sm" data-testid="shifts-table">
            <thead className="text-ui-muted text-[11px] uppercase tracking-wide border-b border-ui-line">
              <tr>
                <th className="text-left py-2 px-3">Kasir</th>
                <th className="text-left py-2 px-3">Buka</th>
                <th className="text-left py-2 px-3">Tutup</th>
                <th className="text-left py-2 px-3">Status</th>
                <th className="text-right py-2 px-3">Transaksi</th>
                <th className="text-right py-2 px-3">Penjualan</th>
                <th className="text-right py-2 px-3">Kas Seharusnya</th>
                <th className="text-right py-2 px-3">Kas Dihitung</th>
                <th className="text-right py-2 px-3">Selisih</th>
                <th className="text-center py-2 px-3">Cetak</th>
              </tr>
            </thead>
            <tbody>
              {shifts.length === 0 ? (
                <tr><td colSpan={10} className="py-10 text-center text-ui-muted"><Clock className="h-6 w-6 mx-auto mb-2 opacity-50" />Belum ada shift pada filter ini</td></tr>
              ) : shifts.map((s) => {
                const sm = s.summary || {};
                const sel = s.selisih ?? 0;
                return (
                  <tr key={s.id} data-testid={`shift-row-${s.id}`} className="border-b border-ui-line hover:bg-ui-soft">
                    <td className="py-2 px-3 font-medium">{s.user_name}</td>
                    <td className="py-2 px-3 text-ui-muted">{dt(s.opened_at)}</td>
                    <td className="py-2 px-3 text-ui-muted">{dt(s.closed_at)}</td>
                    <td className="py-2 px-3">
                      <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${s.status === "open" ? "bg-brand-soft text-brand-readable" : "bg-ui-muted-surface/70 text-ui-muted"}`}>
                        {s.status === "open" ? "Buka" : "Tutup"}
                      </span>
                    </td>
                    <td className="py-2 px-3 text-right font-mono">{sm.tx_count ?? "-"}</td>
                    <td className="py-2 px-3 text-right font-mono">{s.status === "closed" ? rupiah(sm.revenue || 0) : "-"}</td>
                    <td className="py-2 px-3 text-right font-mono">{s.status === "closed" ? rupiah(s.cash_expected || 0) : "-"}</td>
                    <td className="py-2 px-3 text-right font-mono">{s.status === "closed" ? rupiah(s.cash_counted || 0) : "-"}</td>
                    <td className={`py-2 px-3 text-right font-mono font-bold ${sel === 0 ? "text-ui-muted" : sel > 0 ? "text-accent-readable" : "text-danger-readable"}`}>
                      {s.status === "closed" ? rupiah(sel) : "-"}
                    </td>
                    <td className="py-2 px-3 text-center">
                      {s.status === "closed" ? (
                        <button
                          data-testid={`shift-print-${s.id}`}
                          onClick={() => printClosing(s, s.summary, storeName)}
                          title="Cetak laporan tutup shift"
                          className="inline-flex items-center justify-center h-8 w-8 rounded-lg border border-ui-line bg-ui-card text-brand-readable hover:bg-brand-soft hover:border-brand/50 no-select"
                        >
                          <Printer className="h-4 w-4" />
                        </button>
                      ) : <span className="text-ui-muted text-xs">-</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

function Chip({ children, onClick }) {
  return (
    <button onClick={onClick} className="px-2.5 h-8 rounded-lg text-xs font-medium bg-ui-card border border-ui-line hover:border-brand text-ui-text no-select">
      {children}
    </button>
  );
}

function Stat({ label, value, cls = "" }) {
  return (
    <div className="rounded-xl border border-ui-line bg-ui-soft p-4">
      <div className="text-[11px] text-ui-muted uppercase tracking-wide">{label}</div>
      <div className={`font-mono font-extrabold text-xl mt-1 ${cls}`}>{value}</div>
    </div>
  );
}
